import { computed, makeObservable, observable } from 'mobx';
import { apiCommon } from 'src/api/ApiCommon';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { Response200Type } from 'src/api_openapi/generated/openapi_website_cms_getActivePromotedSports';
import { CompetitionId, EventId } from 'src_common/common/websocket2/id/WebsocketId';
import { Common } from 'src/domains/common/Common';
import { EventsCollectionState } from 'src/domains/sportsbook/shared/Types';
import { EventListGroupEventItemType } from 'src_common/common/websocket2/modelsApi/EventsCollectionQuery';

type SportsFiltersType = {
    key: string;
    isActive: boolean;
};

type PromotedSportType = Response200Type;

export type PromotedSportSingleType = {
    competitions_list: Array<CompetitionType>;
    display_order: number;
    id: number;
    is_active: boolean;
    sport_id: string;
    universe: string;
};

export type EventType = {
    display_order: number;
    id2: EventId;
    id: number;
    is_active: boolean;
    sport_id: string;
    universe: string;
    smallEvent: EventListGroupEventItemType;
};

export type CompetitionType = {
    competition_id: CompetitionId;
    display_order: number;
    events_list: Array<EventType>;
    id: number;
    is_active: boolean;
    sport_id: string;
    universe: string;
};

export class PromotedEventsState {
    private readonly activePromotedSportsResource: Resource<Response200Type | null>;

    @observable public openEvents: boolean = true;
    @observable public activeFilterKey: string = 'all';

    public constructor(private readonly common: Common) {
        makeObservable(this);

        this.activePromotedSportsResource = new Resource(async (): Promise<Response200Type | null> => {
            const response = await apiCommon.getActivePromotedSportsElement.run({});
            return response;
        });
    }

    public setOpenEvents = (): void => {
        this.openEvents = !this.openEvents;
    };

    @computed private get promotedSports(): PromotedSportType {
        const response = this.activePromotedSportsResource.get();
        if (response.type === 'ready' && response.value !== null) {
            return response.value;
        }
        return [];
    }

    @computed private get promotedSportsList(): string[] {
        return this.promotedSports.map((sport) => sport.sport_id);
    }

    @computed private get activeEvents(): Record<string, Array<EventListGroupEventItemType>> {
        const eventsForSport: Record<string, Array<EventListGroupEventItemType>> = {};
        for (const sport of this.promotedSportsList) {
            const events = EventsCollectionState.get(this.common).listOfSport(sport).eventItems;
            if (events !== null) {
                eventsForSport[sport] = events;
            }
        }
        return eventsForSport;
    }

    @computed public get possiblePromotedSports(): PromotedSportSingleType[] {
        const possiblePromotedSports: PromotedSportSingleType[] = [];
        for (const sport of this.promotedSports) {
            if (sport.is_active === false) {
                continue;
            }
            const possibleCompetitions: CompetitionType[] = [];
            for (const competition of sport.competitions_list) {
                if (competition.is_active === false) {
                    continue;
                }
                const eventIdsForSport = this.activeEvents[sport.sport_id];
                const promoActiveEvents: EventType[] = [];
                if (eventIdsForSport === undefined) {
                    continue;
                }
                for (const promoEvent of competition.events_list) {
                    if (promoEvent.is_active === false) {
                        continue;
                    }
                    const promosmallEventModel = eventIdsForSport.find((event) => event.id === promoEvent.event_id);
                    if (promosmallEventModel !== undefined) {
                        promoActiveEvents.push({
                            display_order: promoEvent.display_order,
                            id2: promosmallEventModel.id2,
                            id: promoEvent.id,
                            is_active: promoEvent.is_active,
                            sport_id: promoEvent.sport_id,
                            universe: promoEvent.universe,
                            smallEvent: promosmallEventModel,
                        });
                    }
                }
                if (promoActiveEvents.length > 0) {
                    possibleCompetitions.push({
                        competition_id: this.common.models.id.getCompetitionId(competition.competition_id),
                        display_order: competition.display_order,
                        id: competition.id,
                        is_active: competition.is_active,
                        sport_id: competition.sport_id,
                        universe: competition.universe,
                        events_list: promoActiveEvents,
                    });
                }
            }
            if (possibleCompetitions.length > 0) {
                possiblePromotedSports.push({
                    ...sport,
                    competitions_list: possibleCompetitions,
                });
            }
        }
        return possiblePromotedSports;
    }

    @computed public get promotedSportsToShow(): PromotedSportSingleType[] {
        return this.possiblePromotedSports
            .filter((sport) => sport.sport_id === this.activeFilterKey || this.activeFilterKey === 'all')
            .sort((a, b) => (a.display_order > b.display_order ? 1 : -1));
    }

    @computed private get activeSportFilters(): Array<SportsFiltersType> {
        return this.possiblePromotedSports.map((sport) => {
            return { key: sport.sport_id, isActive: true };
        });
    }

    @computed public get sportFiltersAll(): Array<SportsFiltersType> {
        return [{ key: 'all', isActive: true }, ...this.activeSportFilters];
    }

    @computed public get isPromotedSportsDataLoading(): boolean {
        const promotedSportsData = this.activePromotedSportsResource.get();
        return promotedSportsData.type === 'loading';
    }

    public handleToggleIsSportFilterActive = (key: string): void => {
        this.activeFilterKey = key;
    };

    public isFilterActive = (key: string): boolean => {
        return this.activeFilterKey === key;
    };
}
